'use client';

import {
  inferWorkspaceTimedMediaKind,
  resolveWorkspaceTimedMediaAcceptTokens,
} from '@/ai/media/timed-media-input-policy';
import { renderAppIcon } from '@/components/icons/app-icon-registry';
import { PremiumCrownIcon } from '@/components/paid-access/premium-crown-icon';
import { UnifiedTooltip } from '@/components/ui/feedback/notification/unified-tooltip';
import { Spinner } from '@/components/ui/feedback/status/spinner';
import { TransparentAwareImage } from '@/components/ui/media/transparent-aware-image';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/overlay/dropdown-menu';
import { cn } from '@/lib/ui/utils';
import {
  type CSSProperties,
  type ReactNode,
  useId,
  useRef,
  useState,
} from 'react';
import type { WorkspaceToolPrefillFieldView } from '../contracts/tool-view';
import type {
  WorkspaceStudioViewActions,
  WorkspaceStudioViewModel,
} from '../contracts/view-model';
import { useWorkspaceUploadDrop } from './use-workspace-upload-drop';
import { WorkspaceImageMentionInput } from './workspace-image-mention-input';
import { WorkspaceInfoTooltip } from './workspace-info-tooltip';
import { WorkspaceReferenceMediaCard } from './workspace-reference-media-card';
import { shortenThumbDisplayName } from './workspace-studio-view-utils';
import type { WorkspaceUploadRequirements } from './workspace-upload-requirements';
import { WorkspaceUploadRequirementsTooltip } from './workspace-upload-requirements-tooltip';

export function WorkspaceUploadStatus({
  label,
  progress,
  compact = false,
}: {
  label: string;
  progress?: number;
  compact?: boolean;
}) {
  const statusLabel =
    typeof progress === 'number'
      ? `${label} · ${Math.min(100, Math.max(0, Math.round(progress)))}%`
      : label;
  return (
    <output
      className={cn(
        'cms-block-workspace-workspace-1-workspace-form-controls__uploadSingleLoading',
        compact &&
          'cms-block-workspace-workspace-1-workspace-form-controls__uploadSingleLoadingCompact'
      )}
      aria-label={statusLabel}
      aria-live="polite"
    >
      <Spinner
        className={cn(
          'cms-block-workspace-workspace-1-workspace-form-controls__uploadSingleLoadingSpinner',
          compact &&
            'cms-block-workspace-workspace-1-workspace-form-controls__uploadSingleLoadingSpinnerCompact'
        )}
        aria-hidden="true"
        role="presentation"
      />
      <span className="cms-block-workspace-workspace-1-workspace-form-controls__uploadSingleLoadingStatus">
        {statusLabel}
      </span>
    </output>
  );
}

export function WorkspaceUploadTileButton({
  label,
  ariaLabel = label,
  disabled = false,
  onClick,
}: {
  label: string;
  ariaLabel?: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="cms-block-workspace-workspace-1-workspace-form-controls__uploadTile"
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
    >
      {renderAppIcon({
        name: 'upload',
        className:
          'cms-block-workspace-workspace-1-workspace-form-controls__uploadTileIcon',
      })}
      <span className="cms-block-workspace-workspace-1-workspace-form-controls__uploadTileTitle">
        {label}
      </span>
    </button>
  );
}

export function WorkspaceUploadPreviewImage({
  src,
  alt,
  className,
  loadingSkeleton = true,
}: {
  src: string;
  alt: string;
  className: string;
  loadingSkeleton?: boolean;
}) {
  return (
    <TransparentAwareImage
      loadingSkeleton={loadingSkeleton}
      src={src}
      alt={alt}
      className={className}
      loading="eager"
      showCheckerboard
    />
  );
}

export function WorkspaceUploadThumbnailCard({
  previewUrl,
  alt,
  uploadStatus,
  uploadProgress,
  uploadingLabel,
  failedLabel,
  removeLabel,
  bannerLabel,
  hideRemoveWhileUploading = false,
  disabled = false,
  onRemove,
}: {
  previewUrl: string;
  alt: string;
  uploadStatus: 'uploading' | 'uploaded' | 'failed';
  uploadProgress?: number;
  uploadingLabel: string;
  failedLabel?: string;
  removeLabel: string;
  bannerLabel?: string;
  hideRemoveWhileUploading?: boolean;
  disabled?: boolean;
  onRemove: () => void;
}) {
  return (
    <div
      className="cms-block-workspace-workspace-1-workspace-form-controls__uploadThumbCard"
      data-upload-status={uploadStatus}
    >
      {uploadStatus === 'uploading' ? (
        <WorkspaceUploadStatus
          label={uploadingLabel}
          progress={uploadProgress}
          compact
        />
      ) : (
        <WorkspaceUploadPreviewImage
          src={previewUrl}
          alt={alt}
          className="cms-block-workspace-workspace-1-workspace-form-controls__uploadThumbImage"
        />
      )}
      {uploadStatus === 'failed' && failedLabel ? (
        <span className="cms-block-workspace-workspace-1-workspace-form-controls__uploadThumbOverlay">
          {failedLabel}
        </span>
      ) : uploadStatus === 'uploaded' && bannerLabel ? (
        <span className="cms-block-workspace-workspace-1-workspace-image-mention__thumbBanner">
          {bannerLabel}
        </span>
      ) : null}
      {uploadStatus !== 'uploading' || !hideRemoveWhileUploading ? (
        <button
          type="button"
          aria-label={removeLabel}
          className="cms-block-workspace-workspace-1-workspace-form-controls__uploadThumbRemove"
          disabled={disabled}
          onClick={onRemove}
        >
          {renderAppIcon({
            name: 'X',
            className:
              'cms-block-workspace-workspace-1-workspace-form-controls__uploadThumbRemoveIcon',
          })}
        </button>
      ) : null}
    </div>
  );
}

function WorkspaceSingleImageUploadSlot({
  preview,
  isLoading = false,
  uploadProgress,
  readOnly = false,
  disabled = false,
  addDisabled = disabled,
  removeDisabled = disabled,
  isUploaded = Boolean(preview) && !isLoading,
  addLabel,
  addAriaLabel,
  cornerAction,
  bannerLabel,
  onAdd,
  onRemove,
  labels,
  variant = 'default',
}: {
  preview: { id: string; previewUrl: string; alt: string } | null;
  isLoading?: boolean;
  uploadProgress?: number;
  readOnly?: boolean;
  disabled?: boolean;
  addDisabled?: boolean;
  removeDisabled?: boolean;
  isUploaded?: boolean;
  addLabel: string;
  addAriaLabel?: string;
  cornerAction?: ReactNode;
  bannerLabel?: string;
  onAdd: () => void;
  onRemove: (id: string) => void;
  labels: WorkspaceStudioViewModel['shell']['controls'];
  variant?: 'default' | 'frame';
}) {
  return (
    <div
      className={cn(
        'cms-block-workspace-workspace-1-workspace-form-controls__uploadSingleSlot',
        variant === 'frame' &&
          'cms-block-workspace-workspace-1-workspace-form-controls__uploadSingleSlotFrame'
      )}
      data-filled={preview && isUploaded ? 'true' : 'false'}
    >
      {isLoading ? (
        <WorkspaceUploadStatus
          label={labels.canvasCopy.uploading}
          progress={uploadProgress}
        />
      ) : preview && isUploaded ? (
        <>
          <WorkspaceUploadPreviewImage
            src={preview.previewUrl}
            alt={preview.alt}
            className="cms-block-workspace-workspace-1-workspace-form-controls__uploadSinglePreviewImage"
            loadingSkeleton={false}
          />
          {bannerLabel ? (
            <span className="cms-block-workspace-workspace-1-workspace-image-mention__thumbBanner">
              {bannerLabel}
            </span>
          ) : null}
          {!readOnly && (
            <button
              type="button"
              aria-label={labels.uploadRemoveImageLabel}
              className={cn(
                'cms-block-workspace-workspace-1-workspace-form-controls__neutralIconButton',
                'cms-block-workspace-workspace-1-workspace-form-controls__uploadSingleRemove'
              )}
              disabled={removeDisabled}
              onClick={() => onRemove(preview.id)}
            >
              {renderAppIcon({
                name: 'Trash2',
                className:
                  'cms-block-workspace-workspace-1-workspace-form-controls__uploadSingleRemoveIcon',
              })}
            </button>
          )}
        </>
      ) : (
        <WorkspaceUploadTileButton
          label={addLabel}
          ariaLabel={addAriaLabel}
          disabled={addDisabled}
          onClick={onAdd}
        />
      )}
      {!readOnly && cornerAction}
    </div>
  );
}

function resolveBatchModeTooltipText(params: {
  tooltipText: string;
  showPaidFeatureMarkers: boolean;
}) {
  if (params.showPaidFeatureMarkers) {
    return params.tooltipText;
  }

  return params.tooltipText
    .replace(/\s*Available for users with paid access\.?\s*$/i, '')
    .trim();
}

export function WorkspacePrimaryInputPanel({
  label,
  required = false,
  labels,
  accept,
  uploadRequirementsText,
  showUploadRequirements,
  previewItems,
  activePreviewId,
  detailPreviewId,
  showPaidFeatureMarkers,
  batchModeEnabled,
  onToggleBatchMode,
  onFilesChange,
  onSelectPreview,
  onRemovePreview,
  allowMultiple = true,
  assetsAction,
}: {
  label: string;
  required?: boolean;
  labels: WorkspaceStudioViewModel['shell']['controls'];
  accept: string;
  uploadRequirementsText?: string;
  showUploadRequirements: boolean;
  previewItems: WorkspaceStudioViewModel['primaryPreviewItems'];
  activePreviewId: string | null;
  detailPreviewId?: string;
  showPaidFeatureMarkers: boolean;
  batchModeEnabled: boolean;
  onToggleBatchMode: WorkspaceStudioViewActions['handleBatchModeToggle'];
  onFilesChange: WorkspaceStudioViewActions['handlePrimaryFilesChange'];
  onSelectPreview: WorkspaceStudioViewActions['handlePrimaryPreviewSelect'];
  onRemovePreview: WorkspaceStudioViewActions['handlePrimaryPreviewRemove'];
  allowMultiple?: boolean;
  assetsAction?: ReactNode;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const handleUploadTileClick = () => {
    inputRef.current?.click();
  };
  const handleBatchBadgeClick = () => {
    onToggleBatchMode();
  };
  const canSelectMultiplePrimaryFiles = allowMultiple && batchModeEnabled;
  const handlePrimaryFiles = (files: File[]) => {
    if (files.length === 0) {
      return;
    }

    if (detailPreviewId) return;
    onFilesChange(canSelectMultiplePrimaryFiles ? files : files.slice(0, 1));
  };
  const uploadDrop = useWorkspaceUploadDrop(handlePrimaryFiles);
  const batchModeTooltipText = resolveBatchModeTooltipText({
    tooltipText: labels.uploadBatchModeTooltip,
    showPaidFeatureMarkers,
  });
  const usesSingleImageSlot = !batchModeEnabled || Boolean(detailPreviewId);
  const singlePreviewItem = detailPreviewId
    ? (previewItems.find((item) => item.id === detailPreviewId) ?? null)
    : usesSingleImageSlot
      ? (previewItems[0] ?? null)
      : null;
  const singleImageUploadStatus = singlePreviewItem?.uploadStatus ?? null;
  const isSingleImageLoading =
    usesSingleImageSlot && singleImageUploadStatus === 'uploading';
  const isUploadEmpty = !singlePreviewItem;
  const visibleUploadRequirementsText = showUploadRequirements
    ? uploadRequirementsText
    : null;
  const batchModeBadge = allowMultiple ? (
    <div
      className="cms-block-workspace-workspace-1-workspace-form-controls__paramSegmentedSlider cms-workspace-upscale-size__sizeModes"
      data-sliding="true"
      style={
        {
          '--workspace-segment-count': 2,
          '--workspace-segment-index': batchModeEnabled ? 1 : 0,
        } as CSSProperties
      }
    >
      {[false, true].map((enabled) => (
        <button
          key={String(enabled)}
          type="button"
          className="cms-block-workspace-workspace-1-workspace-form-controls__paramSegmentedSliderOption"
          aria-pressed={batchModeEnabled === enabled}
          onClick={() => {
            if (batchModeEnabled !== enabled) handleBatchBadgeClick();
          }}
        >
          {enabled ? labels.batchModeBadgeLabel : labels.batchModeOffBadgeLabel}
          {enabled && showPaidFeatureMarkers ? (
            <PremiumCrownIcon
              className="ms-1 shrink-0"
              iconClassName="h-4 w-4"
              opticalOffsetY="0"
              showTooltip={false}
            />
          ) : null}
        </button>
      ))}
    </div>
  ) : null;

  return (
    <div className="cms-block-workspace-workspace-1-workspace-form-controls__controlGroup">
      <div className="cms-block-workspace-workspace-1-workspace-form-controls__uploadLabelBar">
        <div className="cms-block-workspace-workspace-1-workspace-form-controls__controlLabelRow">
          <div className="cms-block-workspace-workspace-1-workspace-form-controls__controlLabel">
            {label}
          </div>
          {visibleUploadRequirementsText ? (
            <WorkspaceInfoTooltip
              className="cms-block-workspace-workspace-1-workspace-form-controls__paramHintTriggerControl"
              text={visibleUploadRequirementsText}
            />
          ) : null}
          {required ? (
            <span
              className="cms-block-workspace-workspace-1-workspace-form-controls__requiredMark"
              aria-hidden="true"
            >
              *
            </span>
          ) : null}
        </div>
        <div className="cms-block-workspace-workspace-1-workspace-form-controls__uploadLabelActions">
          {batchModeBadge ? (
            <UnifiedTooltip
              triggerAsChild
              trigger={batchModeBadge}
              text={batchModeTooltipText}
              bodyClassName="max-w-72 text-sm leading-6 sm:max-w-80"
              contentClassName="px-3 py-2.5"
            />
          ) : null}
          {!detailPreviewId ? assetsAction : null}
        </div>
      </div>
      <input
        ref={inputRef}
        className="cms-block-workspace-workspace-1-workspace-form-controls__hiddenInput"
        type="file"
        multiple={canSelectMultiplePrimaryFiles}
        accept={accept}
        required={required}
        aria-label={labels.uploadTileLabel}
        aria-required={required}
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          handlePrimaryFiles(files);
          event.currentTarget.value = '';
        }}
      />
      <div
        className={cn(
          'cms-block-workspace-workspace-1-workspace-form-controls__uploadStrip',
          'cms-block-workspace-workspace-1-workspace-form-controls__uploadStripSingle',
          isUploadEmpty &&
            'cms-block-workspace-workspace-1-workspace-form-controls__uploadStripEmpty'
        )}
        {...uploadDrop}
      >
        <div className="cms-block-workspace-workspace-1-workspace-form-controls__uploadStripInner">
          <WorkspaceSingleImageUploadSlot
            readOnly={Boolean(detailPreviewId)}
            preview={
              singlePreviewItem?.previewUrl
                ? {
                    id: singlePreviewItem.id,
                    previewUrl: singlePreviewItem.previewUrl,
                    alt: singlePreviewItem.displayName,
                  }
                : null
            }
            isLoading={isSingleImageLoading}
            uploadProgress={singlePreviewItem?.uploadProgress}
            isUploaded={singleImageUploadStatus === 'uploaded'}
            addLabel={labels.uploadTileLabel}
            labels={labels}
            onAdd={handleUploadTileClick}
            onRemove={onRemovePreview}
          />
        </div>
      </div>
    </div>
  );
}

type WorkspaceImageRoleOption = {
  value: string;
  label: string;
  kind: 'reference' | 'firstFrame' | 'lastFrame';
};

function isWorkspaceImageRoleOptionDisabled(params: {
  image: WorkspaceStudioViewModel['referenceImages'][number];
  option: WorkspaceImageRoleOption;
  options: WorkspaceImageRoleOption[];
  normalReferences: WorkspaceStudioViewModel['referenceImages'];
  frameReferences: NonNullable<WorkspaceStudioViewModel['frameImages']>;
  referenceMediaCount: number;
  frameModeExclusive: boolean;
}) {
  if (params.image.fieldId === params.option.value) return false;

  const firstFrameFieldId = params.options.find(
    (option) => option.kind === 'firstFrame'
  )?.value;
  const lastFrameFieldId = params.options.find(
    (option) => option.kind === 'lastFrame'
  )?.value;
  const firstFrame = params.frameReferences.find(
    (image) => image.fieldId === firstFrameFieldId && !image.isEmpty
  );
  const lastFrame = params.frameReferences.find(
    (image) => image.fieldId === lastFrameFieldId && !image.isEmpty
  );

  if (
    params.option.kind !== 'reference' &&
    params.frameReferences.some(
      (image) =>
        image.id !== params.image.id &&
        !image.isEmpty &&
        image.fieldId === params.option.value
    )
  ) {
    return true;
  }
  if (
    params.image.fieldId === firstFrameFieldId &&
    params.option.kind !== 'firstFrame' &&
    lastFrame
  ) {
    return true;
  }
  if (
    params.option.kind === 'lastFrame' &&
    (!firstFrame || firstFrame.id === params.image.id)
  ) {
    return true;
  }
  if (params.frameModeExclusive && params.option.kind !== 'reference') {
    const hasOtherNormalReference = params.normalReferences.some(
      (image) => image.id !== params.image.id && !image.isEmpty
    );
    return hasOtherNormalReference || params.referenceMediaCount > 0;
  }
  return false;
}

function WorkspaceImageRoleMenu({
  image,
  options,
  normalReferences,
  frameReferences,
  referenceMediaCount,
  frameModeExclusive,
  disabled,
  onChange,
}: {
  image: WorkspaceStudioViewModel['referenceImages'][number];
  options: WorkspaceImageRoleOption[];
  normalReferences: WorkspaceStudioViewModel['referenceImages'];
  frameReferences: NonNullable<WorkspaceStudioViewModel['frameImages']>;
  referenceMediaCount: number;
  frameModeExclusive: boolean;
  disabled: boolean;
  onChange?: (id: string, fieldId: string) => void;
}) {
  if (options.length <= 1 || image.uploadStatus !== 'uploaded') return null;
  const current =
    options.find((option) => option.value === image.fieldId) ?? options[0];
  if (!current) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="cms-block-workspace-workspace-1-workspace-form-controls__mediaRoleTrigger"
          disabled={disabled}
          aria-label={current.label}
        >
          <span>{current.label}</span>
          {renderAppIcon({
            name: 'ChevronDown',
            className:
              'cms-block-workspace-workspace-1-workspace-form-controls__mediaRoleTriggerIcon',
          })}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-36">
        <DropdownMenuRadioGroup
          value={current.value}
          onValueChange={(fieldId) => onChange?.(image.id, fieldId)}
        >
          {options.map((option) => (
            <DropdownMenuRadioItem
              key={option.value}
              value={option.value}
              disabled={isWorkspaceImageRoleOptionDisabled({
                image,
                option,
                options,
                normalReferences,
                frameReferences,
                referenceMediaCount,
                frameModeExclusive,
              })}
            >
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function WorkspaceReferenceComposerPanel({
  assetsPlacement = 'label',
  labels,
  promptLabel,
  promptPlaceholder,
  promptRequired,
  prompt,
  maxPromptCharacters,
  onPromptChange,
  referenceLabel,
  referenceRequired = false,
  frameModeLabel = 'Frames',
  referenceInputMode = 'reference',
  showReferenceInputModeSwitch = false,
  firstFrameFieldId = null,
  lastFrameFieldId = null,
  firstFrameLabel = 'First frame',
  lastFrameLabel = 'Last frame',
  onReferenceInputModeChange,
  onReferenceFrameChange,
  firstFrameAssetsAction,
  lastFrameAssetsAction,
  accept,
  uploadRequirementsText,
  uploadRequirements,
  uploadRequirementsAriaLabel,
  maxReferenceImages,
  showUploadRequirements,
  references,
  referenceMedia = [],
  referenceMediaFields = [],
  onAddReferences,
  onAddReferenceMedia,
  onRemoveReference,
  onRemoveReferenceMedia,
  hidePrompt = false,
  allowMultipleReferences = true,
  referenceDisabled = false,
  frameReferences = [],
  imageRoleOptions = [],
  referenceMediaCount = 0,
  frameModeExclusive = false,
  onReferenceRoleChange,
  assetsAction,
}: {
  assetsPlacement?: 'label' | 'inside';
  labels: WorkspaceStudioViewModel['shell']['controls'];
  promptLabel: string;
  promptPlaceholder: string;
  promptRequired: boolean;
  prompt: string;
  maxPromptCharacters?: number;
  onPromptChange: WorkspaceStudioViewActions['handleComposerPromptChange'];
  referenceLabel: string;
  referenceRequired?: boolean;
  frameModeLabel?: string;
  referenceInputMode?: 'reference' | 'frames';
  showReferenceInputModeSwitch?: boolean;
  firstFrameFieldId?: string | null;
  lastFrameFieldId?: string | null;
  firstFrameLabel?: string;
  lastFrameLabel?: string;
  onReferenceInputModeChange?: (mode: 'reference' | 'frames') => void;
  onReferenceFrameChange?: (fieldId: string, file: File | null) => void;
  firstFrameAssetsAction?: ReactNode;
  lastFrameAssetsAction?: ReactNode;
  accept: string;
  uploadRequirementsText?: string;
  uploadRequirements?: WorkspaceUploadRequirements;
  uploadRequirementsAriaLabel?: string;
  maxReferenceImages: number;
  showUploadRequirements: boolean;
  references: WorkspaceStudioViewModel['referenceImages'];
  referenceMedia?: NonNullable<WorkspaceStudioViewModel['referenceMedia']>;
  referenceMediaFields?: WorkspaceToolPrefillFieldView[];
  onAddReferences: WorkspaceStudioViewActions['handleReferenceImageAdd'];
  onAddReferenceMedia?: WorkspaceStudioViewActions['handleReferenceMediaAdd'];
  onRemoveReference: WorkspaceStudioViewActions['handleReferenceImageRemove'];
  onRemoveReferenceMedia?: WorkspaceStudioViewActions['handleReferenceMediaRemove'];
  hidePrompt?: boolean;
  allowMultipleReferences?: boolean;
  referenceDisabled?: boolean;
  frameReferences?: WorkspaceStudioViewModel['frameImages'];
  imageRoleOptions?: WorkspaceImageRoleOption[];
  referenceMediaCount?: number;
  frameModeExclusive?: boolean;
  onReferenceRoleChange?: (id: string, fieldId: string) => void;
  assetsAction?: ReactNode;
}) {
  const referenceInputId = useId();
  const referenceInputRef = useRef<HTMLInputElement | null>(null);
  const firstFrameInputRef = useRef<HTMLInputElement | null>(null);
  const lastFrameInputRef = useRef<HTMLInputElement | null>(null);
  const [isPromptPolishing, setIsPromptPolishing] = useState(false);
  const resolvedFrameReferences = frameReferences ?? [];
  const supportsFrameMode =
    showReferenceInputModeSwitch &&
    Boolean(firstFrameFieldId) &&
    Boolean(lastFrameFieldId) &&
    Boolean(onReferenceFrameChange);
  const showHistoryInUpload = supportsFrameMode || assetsPlacement === 'inside';
  const firstFrame = resolvedFrameReferences.find(
    (reference) => reference.fieldId === firstFrameFieldId && !reference.isEmpty
  );
  const lastFrame = resolvedFrameReferences.find(
    (reference) => reference.fieldId === lastFrameFieldId && !reference.isEmpty
  );
  const displayReferences = supportsFrameMode
    ? references
    : [...references, ...resolvedFrameReferences];
  const referenceImageCount = references.filter(
    (reference) => !reference.isEmpty
  ).length;
  const remainingReferenceImageSlots = Math.max(
    0,
    maxReferenceImages - referenceImageCount
  );
  const videoField = referenceMediaFields.find(
    (field) => field.control === 'video_file'
  );
  const audioField = referenceMediaFields.find(
    (field) => field.control === 'audio_file'
  );
  const remainingMediaSlots = (
    field: WorkspaceToolPrefillFieldView | undefined
  ) => {
    if (!field) return 0;
    const maxCount = field.mediaConstraints?.maxCount ?? field.maxItems ?? 1;
    const currentCount = referenceMedia.filter(
      (item) => item.fieldId === field.id
    ).length;
    return Math.max(0, maxCount - currentCount);
  };
  const remainingVideoSlots = remainingMediaSlots(videoField);
  const remainingAudioSlots = remainingMediaSlots(audioField);
  const unifiedReferencesEnabled =
    referenceMediaFields.length > 0 && Boolean(onAddReferenceMedia);
  const isReferenceLimitReached =
    remainingReferenceImageSlots === 0 &&
    (!unifiedReferencesEnabled ||
      (remainingVideoSlots === 0 && remainingAudioSlots === 0));
  const unifiedAccept = Array.from(
    new Set([
      ...accept
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean),
      ...referenceMediaFields.flatMap((field) =>
        resolveWorkspaceTimedMediaAcceptTokens(
          field.mediaConstraints?.allowedMimeTypes
        )
      ),
    ])
  ).join(',');

  const isImageFile = (file: File) =>
    file.type.startsWith('image/') ||
    /\.(?:png|jpe?g|webp|gif|avif)$/i.test(file.name);
  const isVideoFile = (file: File) =>
    Boolean(videoField) && inferWorkspaceTimedMediaKind(file) === 'video';
  const isAudioFile = (file: File) =>
    Boolean(audioField) && inferWorkspaceTimedMediaKind(file) === 'audio';

  const handleReferenceFiles = (files: File[]) => {
    if (
      files.length === 0 ||
      isReferenceLimitReached ||
      isPromptPolishing ||
      referenceDisabled
    ) {
      return;
    }

    const filesAllowedByMode = allowMultipleReferences
      ? files
      : files.slice(0, 1);
    if (!unifiedReferencesEnabled) {
      const filesWithinLimit = filesAllowedByMode.slice(
        0,
        remainingReferenceImageSlots
      );
      if (filesWithinLimit.length > 0) onAddReferences(filesWithinLimit);
      return;
    }

    const imageFiles = filesAllowedByMode
      .filter(isImageFile)
      .slice(0, remainingReferenceImageSlots);
    const videoFiles = filesAllowedByMode.filter(isVideoFile);
    const audioFiles = filesAllowedByMode.filter(isAudioFile);

    if (imageFiles.length > 0) onAddReferences(imageFiles);
    if (videoField && videoFiles.length > 0) {
      onAddReferenceMedia?.(videoField.id, videoFiles);
    }
    if (audioField && audioFiles.length > 0) {
      onAddReferenceMedia?.(audioField.id, audioFiles);
    }
  };
  const uploadDrop = useWorkspaceUploadDrop(
    handleReferenceFiles,
    isReferenceLimitReached || isPromptPolishing || referenceDisabled
  );
  const isReferenceSingleUpload =
    !allowMultipleReferences && referenceImageCount <= 1;
  const overReferenceLimit = referenceImageCount > maxReferenceImages;
  const isReferenceUploadEmpty =
    displayReferences.every((reference) => reference.isEmpty) &&
    referenceMedia.length === 0;
  const hasEmptyReferenceSlot = references.some(
    (reference) => reference.isEmpty
  );
  const singleReference = isReferenceSingleUpload
    ? (references[0] ?? null)
    : null;
  const referenceRequirementsText = showUploadRequirements
    ? uploadRequirementsText
    : null;

  return (
    <div
      className="cms-block-workspace-workspace-1-workspace-form-controls__inputModuleStack"
      data-prompt-polishing={isPromptPolishing ? 'true' : undefined}
    >
      {!hidePrompt ? (
        <div className="cms-block-workspace-workspace-1-workspace-form-controls__controlGroup">
          <div className="cms-block-workspace-workspace-1-workspace-form-controls__controlLabelRow">
            <div className="cms-block-workspace-workspace-1-workspace-form-controls__controlLabel">
              {promptLabel}
            </div>
            {promptRequired ? (
              <span
                className="cms-block-workspace-workspace-1-workspace-form-controls__requiredMark"
                aria-hidden="true"
              >
                *
              </span>
            ) : null}
          </div>
          <div className="cms-block-workspace-workspace-1-workspace-form-controls__promptTextareaWrap">
            <WorkspaceImageMentionInput
              ariaLabel={promptLabel}
              placeholder={promptPlaceholder}
              prompt={prompt}
              promptRequired={promptRequired}
              references={referenceInputMode === 'frames' ? [] : references}
              referenceMedia={
                referenceInputMode === 'frames' ? [] : referenceMedia
              }
              polishLabel={labels.promptPolishLabel}
              polishUndoLabel={labels.promptPolishUndoLabel}
              polishSucceededLabel={labels.promptPolishSucceededLabel}
              polishFailedLabel={labels.promptPolishFailedLabel}
              onPolishingChange={setIsPromptPolishing}
              onPromptChange={onPromptChange}
            />
            {typeof maxPromptCharacters === 'number' &&
            maxPromptCharacters > 0 ? (
              <span
                className={cn(
                  'cms-block-workspace-workspace-1-workspace-form-controls__promptCharacterCount',
                  prompt.length > maxPromptCharacters &&
                    'cms-block-workspace-workspace-1-workspace-form-controls__promptCharacterCountOverLimit'
                )}
                aria-live="polite"
              >
                {prompt.length}/{maxPromptCharacters}
              </span>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="cms-block-workspace-workspace-1-workspace-form-controls__controlGroup">
        {supportsFrameMode ? (
          <div
            className="cms-block-workspace-workspace-1-workspace-form-controls__referenceModeToolbar"
            data-mode={referenceInputMode}
          >
            <div
              className="cms-block-workspace-workspace-1-workspace-form-controls__paramSegmentedSlider cms-workspace-upscale-size__sizeModes cms-block-workspace-workspace-1-workspace-form-controls__referenceModeSegmented"
              data-sliding="true"
              aria-label={referenceLabel}
              style={
                {
                  '--workspace-segment-count': 2,
                  '--workspace-segment-index':
                    referenceInputMode === 'frames' ? 1 : 0,
                } as CSSProperties
              }
            >
              {(
                [
                  ['reference', referenceLabel],
                  ['frames', frameModeLabel],
                ] as const
              ).map(([mode, label]) => (
                <button
                  key={mode}
                  type="button"
                  className="cms-block-workspace-workspace-1-workspace-form-controls__paramSegmentedSliderOption"
                  aria-pressed={referenceInputMode === mode}
                  data-selected={
                    referenceInputMode === mode ? 'true' : undefined
                  }
                  onClick={() => onReferenceInputModeChange?.(mode)}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="cms-block-workspace-workspace-1-workspace-form-controls__referenceModeActions">
              {uploadRequirements && uploadRequirementsAriaLabel ? (
                <WorkspaceUploadRequirementsTooltip
                  ariaLabel={uploadRequirementsAriaLabel}
                  requirements={uploadRequirements}
                />
              ) : referenceRequirementsText ? (
                <WorkspaceInfoTooltip
                  ariaLabel={uploadRequirementsAriaLabel}
                  className="cms-block-workspace-workspace-1-workspace-form-controls__paramHintTriggerControl"
                  text={referenceRequirementsText}
                />
              ) : null}
              {referenceInputMode === 'reference' && assetsAction ? (
                <div className="cms-block-workspace-workspace-1-workspace-form-controls__referenceModeHistoryAction">
                  {assetsAction}
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="cms-block-workspace-workspace-1-workspace-form-controls__uploadLabelBar">
            <div className="cms-block-workspace-workspace-1-workspace-form-controls__controlLabelRow">
              <div className="cms-block-workspace-workspace-1-workspace-form-controls__controlLabel">
                {referenceLabel}
              </div>
              {referenceRequirementsText ? (
                <WorkspaceInfoTooltip
                  className="cms-block-workspace-workspace-1-workspace-form-controls__paramHintTriggerControl"
                  text={referenceRequirementsText}
                />
              ) : null}
              {referenceRequired ? (
                <span
                  className="cms-block-workspace-workspace-1-workspace-form-controls__requiredMark"
                  aria-hidden="true"
                >
                  *
                </span>
              ) : null}
            </div>
            {!showHistoryInUpload ? assetsAction : null}
          </div>
        )}
        {supportsFrameMode && referenceInputMode === 'frames' ? (
          <>
            <input
              ref={firstFrameInputRef}
              className="cms-block-workspace-workspace-1-workspace-form-controls__hiddenInput"
              type="file"
              accept={accept}
              aria-label={firstFrameLabel}
              disabled={isPromptPolishing}
              onChange={(event) => {
                const file = event.currentTarget.files?.[0] ?? null;
                if (firstFrameFieldId && file) {
                  onReferenceFrameChange?.(firstFrameFieldId, file);
                }
                event.currentTarget.value = '';
              }}
            />
            <input
              ref={lastFrameInputRef}
              className="cms-block-workspace-workspace-1-workspace-form-controls__hiddenInput"
              type="file"
              accept={accept}
              aria-label={lastFrameLabel}
              disabled={isPromptPolishing}
              onChange={(event) => {
                const file = event.currentTarget.files?.[0] ?? null;
                if (lastFrameFieldId && file) {
                  onReferenceFrameChange?.(lastFrameFieldId, file);
                }
                event.currentTarget.value = '';
              }}
            />
            <div className="cms-block-workspace-workspace-1-workspace-form-controls__referenceFrameGrid">
              <div className="cms-block-workspace-workspace-1-workspace-form-controls__referenceFrameItem">
                <WorkspaceSingleImageUploadSlot
                  preview={
                    firstFrame
                      ? {
                          id: firstFrame.id,
                          previewUrl: firstFrame.previewUrl,
                          alt: firstFrameLabel,
                        }
                      : null
                  }
                  isLoading={firstFrame?.uploadStatus === 'uploading'}
                  uploadProgress={firstFrame?.uploadProgress}
                  isUploaded={firstFrame?.uploadStatus === 'uploaded'}
                  disabled={isPromptPolishing}
                  addLabel={firstFrameLabel}
                  addAriaLabel={firstFrameLabel}
                  labels={labels}
                  cornerAction={firstFrameAssetsAction}
                  variant="frame"
                  onAdd={() => firstFrameInputRef.current?.click()}
                  onRemove={() => {
                    if (firstFrameFieldId) {
                      onReferenceFrameChange?.(firstFrameFieldId, null);
                    }
                  }}
                />
              </div>
              <div className="cms-block-workspace-workspace-1-workspace-form-controls__referenceFrameItem">
                <WorkspaceSingleImageUploadSlot
                  preview={
                    lastFrame
                      ? {
                          id: lastFrame.id,
                          previewUrl: lastFrame.previewUrl,
                          alt: lastFrameLabel,
                        }
                      : null
                  }
                  isLoading={lastFrame?.uploadStatus === 'uploading'}
                  uploadProgress={lastFrame?.uploadProgress}
                  isUploaded={lastFrame?.uploadStatus === 'uploaded'}
                  disabled={isPromptPolishing}
                  addLabel={lastFrameLabel}
                  addAriaLabel={lastFrameLabel}
                  labels={labels}
                  cornerAction={lastFrameAssetsAction}
                  variant="frame"
                  onAdd={() => lastFrameInputRef.current?.click()}
                  onRemove={() => {
                    if (lastFrameFieldId) {
                      onReferenceFrameChange?.(lastFrameFieldId, null);
                    }
                  }}
                />
              </div>
            </div>
          </>
        ) : null}
        <input
          id={referenceInputId}
          ref={referenceInputRef}
          className="cms-block-workspace-workspace-1-workspace-form-controls__hiddenInput"
          type="file"
          multiple={allowMultipleReferences}
          accept={unifiedReferencesEnabled ? unifiedAccept : accept}
          required={referenceRequired}
          aria-label={labels.addReferenceImageLabel}
          aria-required={referenceRequired}
          disabled={
            referenceInputMode === 'frames' ||
            isReferenceLimitReached ||
            isPromptPolishing ||
            referenceDisabled
          }
          onChange={(event) => {
            const files = Array.from(event.target.files ?? []);
            handleReferenceFiles(files);
            event.currentTarget.value = '';
          }}
        />
        <div
          hidden={referenceInputMode === 'frames'}
          className={cn(
            'cms-block-workspace-workspace-1-workspace-form-controls__uploadStrip',
            supportsFrameMode &&
              'cms-block-workspace-workspace-1-workspace-form-controls__referenceModeUploadStrip',
            isReferenceSingleUpload &&
              'cms-block-workspace-workspace-1-workspace-form-controls__uploadStripSingle',
            isReferenceUploadEmpty &&
              'cms-block-workspace-workspace-1-workspace-form-controls__uploadStripEmpty'
          )}
          {...uploadDrop}
        >
          {!isReferenceSingleUpload &&
          showHistoryInUpload &&
          !supportsFrameMode &&
          assetsAction ? (
            <div className="cms-block-workspace-workspace-1-workspace-form-controls__uploadStripCornerAction">
              {assetsAction}
            </div>
          ) : null}
          <div className="cms-block-workspace-workspace-1-workspace-form-controls__uploadStripInner">
            {isReferenceSingleUpload ? (
              <WorkspaceSingleImageUploadSlot
                preview={
                  singleReference
                    ? {
                        id: singleReference.id,
                        previewUrl: singleReference.previewUrl,
                        alt: labels.referenceImageAlt,
                      }
                    : null
                }
                isLoading={singleReference?.uploadStatus === 'uploading'}
                uploadProgress={singleReference?.uploadProgress}
                disabled={isPromptPolishing || referenceDisabled}
                isUploaded={singleReference?.uploadStatus === 'uploaded'}
                addLabel={labels.addReferenceImageLabel}
                addAriaLabel={labels.addReferenceImageLabel}
                labels={labels}
                cornerAction={
                  showHistoryInUpload && !supportsFrameMode
                    ? assetsAction
                    : undefined
                }
                bannerLabel={singleReference ? 'Image 1' : undefined}
                onAdd={() => referenceInputRef.current?.click()}
                onRemove={onRemoveReference}
              />
            ) : (
              <>
                {!isReferenceLimitReached && !hasEmptyReferenceSlot ? (
                  <WorkspaceUploadTileButton
                    label={labels.addReferenceImageLabel}
                    ariaLabel={labels.addReferenceImageLabel}
                    disabled={isPromptPolishing || referenceDisabled}
                    onClick={() => referenceInputRef.current?.click()}
                  />
                ) : null}

                {displayReferences.map((reference) => {
                  if (reference.isEmpty) {
                    return (
                      <WorkspaceUploadTileButton
                        key={reference.id}
                        label={labels.addReferenceImageLabel}
                        ariaLabel={labels.addReferenceImageLabel}
                        disabled={isPromptPolishing || referenceDisabled}
                        onClick={() => referenceInputRef.current?.click()}
                      />
                    );
                  }
                  const normalReferenceIndex = references
                    .filter((item) => !item.isEmpty)
                    .findIndex((item) => item.id === reference.id);
                  const currentRole = imageRoleOptions.find(
                    (option) => option.value === reference.fieldId
                  );
                  const bannerLabel =
                    currentRole?.kind === 'reference' ||
                    normalReferenceIndex >= 0
                      ? `Image ${normalReferenceIndex + 1}`
                      : currentRole?.label;
                  return (
                    <div
                      key={reference.id}
                      className="cms-block-workspace-workspace-1-workspace-form-controls__uploadThumbStack"
                    >
                      <WorkspaceUploadThumbnailCard
                        previewUrl={reference.previewUrl}
                        alt={labels.referenceImageAlt}
                        uploadStatus={reference.uploadStatus}
                        uploadProgress={reference.uploadProgress}
                        uploadingLabel={labels.canvasCopy.uploading}
                        failedLabel={
                          reference.errorMessage ?? labels.canvasCopy.failure
                        }
                        removeLabel={labels.uploadRemoveThumbnailLabel}
                        disabled={isPromptPolishing}
                        hideRemoveWhileUploading
                        bannerLabel={bannerLabel}
                        onRemove={() => onRemoveReference(reference.id)}
                      />
                      <WorkspaceImageRoleMenu
                        image={reference}
                        options={imageRoleOptions}
                        normalReferences={references}
                        frameReferences={resolvedFrameReferences}
                        referenceMediaCount={referenceMediaCount}
                        frameModeExclusive={frameModeExclusive}
                        disabled={isPromptPolishing}
                        onChange={onReferenceRoleChange}
                      />
                    </div>
                  );
                })}
                {unifiedReferencesEnabled
                  ? referenceMedia.map((item) => {
                      const sameKind = referenceMedia.filter(
                        (candidate) => candidate.kind === item.kind
                      );
                      const kindIndex = sameKind.findIndex(
                        (candidate) => candidate.id === item.id
                      );
                      return (
                        <WorkspaceReferenceMediaCard
                          key={item.id}
                          item={item}
                          uploadingLabel={labels.canvasCopy.uploading}
                          removeLabel={labels.uploadRemoveThumbnailLabel}
                          bannerLabel={`${
                            item.kind === 'video' ? 'Video' : 'Audio'
                          } ${kindIndex + 1}`}
                          onRemove={onRemoveReferenceMedia}
                        />
                      );
                    })
                  : null}
              </>
            )}
          </div>
        </div>
        {referenceInputMode === 'reference' && overReferenceLimit ? (
          <output className="cms-block-workspace-workspace-1-workspace-form-controls__referenceLimitWarning">
            {labels.referenceLimitExceededMessage
              .replace('{max}', String(maxReferenceImages))
              .replace(
                '{excess}',
                String(referenceImageCount - maxReferenceImages)
              )}
          </output>
        ) : null}
      </div>
    </div>
  );
}
